import { useInfiniteQuery } from "react-query";
import axios from "axios";

/**
 * Lazy-load preview rows for direct data import.
 * Each page response: { rows, skip (next), totalDataLength }
 * `skip` is undefined on the last page — react-query then stops paging.
 *
 * Backend stores the parsed file as a Redis LIST (seeded by the prior
 * /processExcelForDirectDataImport call); each page fetch is an LRANGE
 * over the requested slice — no full-file deserialization per request.
 */
export const useDirectImportPreviewRowsLazy = ({
  fileNameInGcs,
  autoInjectRowIdKey,
  sheetName,
  limit = 30,
  initialFirstPage,
  queryOptions,
}) => {
  return useInfiniteQuery({
    // sheetName is part of the key: the same file paged against a different
    // sheet is a different result set, and staleTime: Infinity would otherwise
    // hand back the previous sheet's pages.
    queryKey: ["directImportPreviewRows", fileNameInGcs, sheetName, limit],
    queryFn: ({ pageParam: skip = 0 }) =>
      axios
        .post("/api/content/getDirectDataImportPreviewRows", {
          fileNameInGcs,
          limit,
          skip,
          autoInjectRowIdKey,
          ...(sheetName && { sheetName }),
        })
        .then((res) => res.data),
    getNextPageParam: (lastPage) => lastPage.skip,
    enabled: !!fileNameInGcs,
    // Once data is loaded for the modal session, no need to refetch
    // — the underlying Redis-cached file is stable for the TTL window.
    staleTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    ...(initialFirstPage
      ? {
          initialData: {
            pages: [initialFirstPage],
            pageParams: [0],
          },
        }
      : {}),
    ...(queryOptions || {}),
  });
};
